import { useEffect, useState } from "react";
import { useLocalization } from "@fluent/react";
import {
  useBreakpoints,
  Table,
  Button,
  Dialog,
  Chip,
} from "@narmi/design_system";
import { modules } from "byzantine";
import Pagination from "byzantine/src/Pagination";
import { EXPEDITE_CHOICE_TO_SPEED } from "byzantine/src/dbbl/businessLogic/entities/bulkPayments";
import type { FormattedBulkTemplate } from "byzantine/src/dbbl/businessLogic/entities/bulkTemplates/types";
import { useUserFeatures } from "../../../contexts/UserFeaturesContext";
import {
  DIALOG_PAGE_SIZE,
  getDialogWidth,
  formatPaymentAmount,
} from "../../../PaymentActivity/common";
import { SPEED_COPY_CONFIG } from "../../../PaymentActivity/CreateBulk/table/BulkPaymentRow";
import { getRecipientById } from "../../../PaymentActivity/CreateBulk/table/BulkPaymentTable";
import PageNavigation from "../../../page_navigation/PageNavigation";
import styles from "./Drawer.module.scss";

type ViewPaymentsDialogProps = {
  isOpen: boolean;
  handleClose: () => void;
  template: FormattedBulkTemplate;
  variant: "version" | "template";
};

const getSpeedLabel = (payment: API.BulkTemplatePaymentData) => {
  const speedKey =
    EXPEDITE_CHOICE_TO_SPEED[payment.expedite_choice] ?? "standard";
  const config = SPEED_COPY_CONFIG[speedKey];
  const isCollect = payment.transaction_type === "debit";
  return {
    label: isCollect ? config.collecting : config.sending,
    icon: config.icon,
  };
};

const HEADER_CLASS = "fontSize--xs fontWeight--bold fontColor--heading";
const CELL_CLASS = "fontSize--m fontWeight--normal fontColor--primary";

const ViewPaymentsDialog = ({
  isOpen,
  handleClose,
  template,
  variant,
}: ViewPaymentsDialogProps) => {
  const { l10n } = useLocalization();
  const { m, l } = useBreakpoints();
  const features = useUserFeatures();
  const {
    expedited_payments: expeditedPaymentsFeature,
    ach_payments_collect: achPaymentsCollectFeature,
  } = features;
  const recipients = modules.achPayments.usePermissionedRecipients();
  const displaySpeed = Boolean(expeditedPaymentsFeature);

  const [pageNumber, setPageNumber] = useState(1);

  useEffect(() => {
    if (isOpen) {
      setPageNumber(1);
    }
  }, [isOpen]);

  const {
    name,
    institutionAccount,
    entryDesc,
    secCode,
    maxExpediteChoice,
    description,
    paymentData,
  } = template;

  const pagination = new Pagination({
    total: paymentData.length,
    page: pageNumber,
    pageSize: DIALOG_PAGE_SIZE,
    navigatePage: (page: number) => {
      setPageNumber(page);
    },
  });

  const startIndex = (pagination.page - 1) * pagination.pageSize;
  const endIndex = startIndex + pagination.pageSize;
  const currentPayments = paymentData.slice(startIndex, endIndex);

  const columns = [
    {
      label: achPaymentsCollectFeature
        ? l10n.getString("label-to-from")
        : l10n.getString("ach-payment-recipient-title"),
    },
    { label: l10n.getString("label-action") },
    ...(displaySpeed ? [{ label: l10n.getString("speed-label") }] : []),
    { label: l10n.getString("transaction-detail-description") },
    { label: l10n.getString("label-amount") },
  ];

  const colVisibility: React.ComponentProps<typeof Table>["colVisibility"] =
    displaySpeed ? ["*", "*", "*", "m", "*"] : ["*", "*", "m", "*"];

  const colLayout: React.ComponentProps<typeof Table>["colLayout"] =
    displaySpeed
      ? {
          s: "1fr 1fr max-content 1fr",
          m: "2fr 1fr max-content 2fr 1fr",
          l: "2fr 1fr 2fr 2fr 1fr",
        }
      : {
          s: "1fr 1fr 1fr",
          m: "2fr 1fr 2fr 1fr",
          l: "2fr 1fr 2fr 1fr",
        };

  const getAction = (payment: API.BulkTemplatePaymentData) =>
    payment.transaction_type === "debit"
      ? l10n.getString("collect-label")
      : l10n.getString("send-label");



  const originatingAccountDisplay = institutionAccount
    ? `${institutionAccount.name}-${institutionAccount.getMaskedNumber()}`
    : "";

  const speedDisplay =
    maxExpediteChoice > 0
      ? l10n.getString("label-allowed-to-expedite")
      : l10n.getString("label-not-allowed-to-expedite");

  const detailRows: Array<[string, string]> = [
    [l10n.getString("label-template-name"), name],
    [l10n.getString("label-originating-account"), originatingAccountDisplay],
    [l10n.getString("label-purpose-payment"), entryDesc ?? ""],
    [l10n.getString("label-payment-type"), secCode ?? ""],
    [l10n.getString("speed-label"), speedDisplay],
    [l10n.getString("label-note-internal"), description ?? ""],
  ].filter(([, value]) => Boolean(value)) as Array<[string, string]>;

  return (
    <Dialog
      isOpen={isOpen}
      footer={
        <div className={styles.dialogFooter}>
          <Button onClick={handleClose}>{l10n.getString("label-close")}</Button>
        </div>
      }
      onUserDismiss={handleClose}
      title={
        variant === "version"
          ? l10n.getString("label-version-details")
          : l10n.getString("label-template-details")
      }
      width={getDialogWidth({ m, l })}
    >
      {variant === "version" && (
        <div
          className={`${styles.detailGrid} margin--top--l margin--bottom--l`}
        >
          {detailRows.map(([label, value], i) => (
            <div key={i} className="margin--bottom--s">
              <span className="fontSize--s fontWeight--normal fontColor--secondary">
                {label}
              </span>
              <div className="fontSize--m fontWeight--normal fontColor--heading">
                {value}
              </div>
            </div>
          ))}
        </div>
      )}

      <Table colVisibility={colVisibility} colLayout={colLayout}>
        <Table.Header>
          <Table.Row>
            {columns.map((col, i) => (
              <Table.HeaderCell key={i}>
                <span className={`${styles.headerCell} ${HEADER_CLASS}`}>
                  {col.label}
                </span>
              </Table.HeaderCell>
            ))}
          </Table.Row>
        </Table.Header>
        <Table.Body>
          {currentPayments.map((payment, index) => {
            const recipient = getRecipientById(
              recipients,
              payment.recipient as API.RecipientId
            );
            const speed = getSpeedLabel(payment);

            return (
              <Table.Row key={`payment-row__${index}`}>
                <Table.Cell>
                  <span className={CELL_CLASS}>{recipient?.name ?? "-"}</span>
                </Table.Cell>
                <Table.Cell>
                  <span className={CELL_CLASS}>{getAction(payment)}</span>
                </Table.Cell>
                {displaySpeed && (
                  <Table.Cell>
                    <Chip
                      kind="info"
                      label={speed.label}
                      startIcon={speed.icon}
                    />
                  </Table.Cell>
                )}
                <Table.Cell>
                  <span className={CELL_CLASS}>{payment.description}</span>
                </Table.Cell>
                <Table.Cell>
                  <span className={CELL_CLASS}>
                    {formatPaymentAmount({
                      amount: payment.amount,
                      isCollect: payment.transaction_type === "debit",
                      achPaymentsCollectFeature: Boolean(achPaymentsCollectFeature),
                    })}
                  </span>
                </Table.Cell>
              </Table.Row>
            );
          })}
        </Table.Body>
      </Table>
      {paymentData.length > DIALOG_PAGE_SIZE && (
        <PageNavigation
          pagination={pagination}
          marginClasses="margin--all--l"
        />
      )}
    </Dialog>
  );
};

export default ViewPaymentsDialog;
